import { api } from './Api';
import { requirePortalAccess } from './PortalSession';

const positive = (id) => Number.isSafeInteger(Number(id)) && /^[1-9]\d*$/.test(String(id));
export const validateIssuePage = (page, businessId, orderId) => {
  if (!page || !Array.isArray(page.results) || typeof page.has_more !== 'boolean' ||
      typeof page.mode !== 'string' || !page.mode || typeof page.next_cursor !== 'string' ||
      !page.next_cursor || page.results.some((row) =>
        !['id', 'attempt_id', 'order_id', 'restaurant_id', 'business_id', 'change_id'].every((key) => positive(row[key])) ||
        Number(row.business_id) !== Number(businessId) ||
        (orderId != null && Number(row.order_id) !== Number(orderId)) ||
        !['open', 'resolved'].includes(row.state) ||
        typeof row.category !== 'string' || typeof row.message !== 'string')) {
    throw new Error('Invalid delivery issues response');
  }
  return page;
};
export const getDeliveryIssues = async ({ businessId, orderId, cursor, signal }) => {
  requirePortalAccess();
  if (!positive(businessId) || (orderId != null && !positive(orderId))) throw new Error('Invalid issue scope');
  const page = await api.get(orderId == null ? '/deliveryops/delivery-issues' :
    `/deliveryops/orders/${orderId}/delivery-issues`, {
    business_id: businessId, state: orderId == null ? 'open' : 'all', limit: 50,
    ...(cursor ? { cursor } : {}),
  }, { cache: 'no-store', signal });
  return validateIssuePage(page, businessId, orderId);
};

export const mergeIssues = (target, rows) => {
  rows.forEach((row) => {
    if (!target.has(row.id) || target.get(row.id).change_id < row.change_id) target.set(row.id, row);
  });
};

// Each instance belongs to one authenticated scope lifetime. Never persist cursors alone.
export class DeliveryIssueMonitor {
  constructor({ businesses, request = getDeliveryIssues, notify, current = () => true,
    now = Date.now, random = Math.random }) {
    Object.assign(this, { request, notify, current, now, random });
    this.scopes = businesses.map(({ id }) => ({
      id, records: new Map(), staging: new Map(), cursor: null, status: 'loading',
      lastSuccess: null, startedAt: this.now(), due: 0, failures: 0, busy: false, stale: false,
    }));
    this.running = 0;
    this.active = true;
    this.disposed = false;
  }
  emit() {
    if (!this.disposed && this.current()) this.notify(this.scopes.map((s) => ({
      id: s.id, status: s.status,
      stale: s.stale || this.now() - (s.lastSuccess == null ? s.startedAt : s.lastSuccess) >= 15000,
      issues: Array.from(s.records.values()).filter((i) => i.state === 'open'),
    })));
  }
  tick() {
    if (this.disposed || !this.current()) return;
    this.emit();
    if (!this.active) return;
    this.scopes.slice().sort((a, b) => a.due - b.due).forEach((s) => {
      if (this.running < 3 && !s.busy && s.status !== 'forbidden' && s.due <= this.now()) this.drain(s);
    });
  }
  async drain(s) {
    s.busy = true;
    this.running += 1;
    const controller = new AbortController();
    s.controller = controller;
    try {
      for (let pageNumber = 0; pageNumber < 10; pageNumber += 1) {
        const page = await this.request({ businessId: s.id, cursor: s.cursor, signal: controller.signal });
        if (this.disposed || !this.current()) return;
        if ((!s.cursor || s.staging) && page.mode !== 'snapshot') throw new Error('Snapshot required');
        if (page.has_more && page.next_cursor === s.cursor) throw new Error('Issue cursor did not advance');
        mergeIssues(s.staging || s.records, page.results);
        s.cursor = page.next_cursor;
        if (!page.has_more) {
          if (s.staging) { s.records = s.staging; s.staging = null; }
          s.lastSuccess = this.now();
          s.stale = false;
          s.failures = 0;
          s.status = 'success';
          s.due = this.now() + 5000;
          break;
        }
        s.due = this.now() + 1;
        if (!this.active) break;
      }
    } catch (error) {
      if (this.disposed || !this.current()) return;
      s.stale = true;
      s.status = error.status === 403 ? 'forbidden' : 'error';
      if (error.status === 403) {
        s.records.clear();
        s.staging = null;
        s.cursor = null;
      }
      if (error.status === 409) { s.cursor = null; s.staging = new Map(); }
      s.due = this.now() + Math.min(30000, 5000 * (2 ** s.failures++)) + this.random() * 1000;
    } finally {
      s.busy = false;
      this.running -= 1;
      this.emit();
    }
  }
  dispose() {
    this.disposed = true;
    this.scopes.forEach((s) => {
      if (s.controller) s.controller.abort();
      s.records.clear();
      if (s.staging) s.staging.clear();
    });
  }
}
